import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Search, X } from 'lucide-react';
import {getImageUrl} from '../../utils/imageUtils';

const MobileMenu = ({ 
  showMobileMenu, 
  setShowMobileMenu, 
  user, 
  favoritesCount,
  unreadNotifications,
  unreadMessages, // Added unreadMessages prop
  onSellClick 
}) => {
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const [mobileSearchQuery, setMobileSearchQuery] = useState('');
  
  // Focus input when menu opens
  useEffect(() => {
    if (showMobileMenu && searchInputRef.current) {
      // Small delay ensures menu is fully rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [showMobileMenu]);

  const handleMobileSearch = (e) => {
    e.preventDefault();
    if (mobileSearchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(mobileSearchQuery.trim())}`);
      setShowMobileMenu(false);
      setMobileSearchQuery(''); // Clear after search
    }
  };

  const handleClose = () => {
    setShowMobileMenu(false);
    setMobileSearchQuery(''); // Clear on close
  };

  if (!showMobileMenu) return null;

  return (
    <div className="fixed inset-0 bg-[#f5f5f5] z-50 md:hidden overflow-y-auto">
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-white">
          <div className="flex items-center">
            <div className="bg-[#ff660e] p-2 rounded-lg mr-3">
              <ShoppingCart className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">EcoMart</h1>
              <p className="text-xs text-gray-500">Buy Smart. Sell Safe.</p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2">
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Search Bar (Mobile Only) */}
        <div className="p-4 bg-white border-b">
          <form onSubmit={handleMobileSearch} className="relative">
            <input
              ref={searchInputRef}
              type="text"
              value={mobileSearchQuery}
              onChange={(e) => setMobileSearchQuery(e.target.value)}
              placeholder="Search products, stores, categories..."
              className="w-full pl-4 pr-12 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#ff660e]"
            />
            <button
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-600 hover:text-[#ff660e]"
            >
              <Search className="w-5 h-5" />
            </button>
          </form>
        </div>

        {/* User Section */}
        <div className="p-4 border-b bg-white">
          {user ? (
            <div 
              className="flex items-center cursor-pointer"
              onClick={() => navigate("/profile")}
            >
              {user.profile_picture_url ? (
                <img 
                  src={getImageUrl(user.profile_picture_url)}
                  alt={`${user.first_name || 'User'}'s profile`}
                  className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm mr-3"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'w-12 h-12 bg-[#ff660e] rounded-full flex items-center justify-center text-white text-xl font-bold mr-3';
                    fallback.textContent = user.first_name?.[0] || 'U';
                    e.target.parentNode.insertBefore(fallback, e.target);
                  }}
                />
              ) : (
                <div className="w-12 h-12 bg-[#ff660e] rounded-full flex items-center justify-center text-white text-xl font-bold mr-3">
                  {user.first_name?.[0] || 'U'}
                </div>
              )}
              <div>
                <p className="font-semibold">Hi, {user.first_name || 'User'}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>
            </div>
          ) : (
            <div className="flex space-x-3">
              <Link to="/login" onClick={handleClose} className="flex-1 text-center py-3 border-2 border-[#ff660e] text-[#ff660e] rounded-lg font-medium">
                Login
              </Link>
              <Link to="/register" onClick={handleClose} className="flex-1 text-center py-3 bg-[#ff660e] text-white rounded-lg font-medium">
                Register
              </Link>
            </div>
          )}
        </div>


        {/* Menu Items */}
        <div className="flex-1 py-4">
          <div className="space-y-1 px-4">
            {[
              { to: '/', label: 'Home', icon: '🏠' },
              { to: '/categories', label: 'Categories', icon: '📁' },
              { to: '/favorites', label: 'Favorites', icon: '❤️', count: favoritesCount },
              { to: '/chats', label: 'Chat', icon: '💬', count: unreadMessages },
              { to: '/notifications', label: 'Notifications', icon: '🔔', count: unreadNotifications },
              { to: '/support', label: 'Support', icon: '🆘' }, // Added support link
              { to: '/profile', label: 'Profile', icon: '👤' },
              { to: '/store', label: 'My Store', icon: '🏪' },
            ].map(item => (
              <Link
                key={item.to}
                to={item.to}
                onClick={handleClose}
                className="flex items-center px-4 py-3 rounded-lg hover:bg-white bg-white shadow-sm mb-2"
              >
                <span className="text-2xl mr-4">{item.icon}</span>
                <span className="flex-1 font-medium">{item.label}</span>
                {item.count > 0 && (
                  <span className="ml-auto bg-[#ff660e] text-white text-xs rounded-full px-2 py-1 min-w-6 text-center">
                    {item.count}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t p-4 bg-white">
          <button
            onClick={() => { 
              handleClose(); 
              onSellClick(); 
            }}
            className="w-full py-4 bg-[#ff660e] text-white rounded-xl font-bold text-lg hover:opacity-90"
          >
            SELL ON ECOMART
          </button>
          <p className="text-center text-sm text-gray-500 mt-4">
            © {new Date().getFullYear()} EcoMart Nigeria
          </p>
        </div>
      </div>
    </div>
  );
};

export default MobileMenu;